import { useCallback, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Button from 'components/Button'
import {
  Report19CreateFormSchemas,
  Report19CSVDataSchemas,
  Report19CreateFormTypes,
  Report19CSVDataTypes,
} from 'schemas/report-19-schemas'

import { useReport19Mutation } from './use-report-19'
import { ExportToXLSX } from '../../utils-reports-xlsx-actions'

export const CreateReport19Form = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const resultRef = useRef<HTMLDivElement | null>(null)

  const date = new Date().toISOString()
  const formDefaultValues: Report19CreateFormTypes = {
    startDate: date,
    endDate: date,
    test: 0,
    fromTraderId: null,
    reportTypeId: 4,
  }

  const form = useForm<Report19CreateFormTypes>({
    resolver: zodResolver(Report19CreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const {
    data: report19Data,
    mutateAsync: postReport19Mutation,
    isSuccess,
    isError,
    isLoading,
  } = useReport19Mutation()

  const onSubmit = useCallback(
    async (formData: Report19CreateFormTypes) => {
      const formattedData = {
        ...formData,
        startDate: `${formData.startDate} 00:00:00`,
        endDate: `${formData.endDate} 23:59:59`,
      }
      postReport19Mutation(formattedData)
    },
    [postReport19Mutation]
  )

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.startDate?.message}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.endDate?.message}
            />
          )}
        />
      </div>
      <div className="flex gap-4 justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoading}
        >
          <span className="tracking-wider font-bold">
            {isLoading ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>

        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>

      {isError || (isSuccess && !report19Data?.data.length) ? (
        <div
          ref={resultRef}
          className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
        >
          <div>
            <p className="tracking-wide text-red font-light">
              {`Sorry, there are no items available.`}
            </p>
          </div>
        </div>
      ) : null}

      {isSuccess && report19Data?.data.length ? (() => {
        const csvKeys = Object.keys(Report19CSVDataSchemas.shape)
        const headerMapping: Record<string, string> = {
          salesCentre: 'Centre',
          salesCentreUser: 'User',
          ident: 'CardNumber',
          transactionDate: 'TransactionDate',
          giftCardSalesSum: 'GiftCardSales',
          serviceFees: 'ServiceFees',
          greetingCardFees: 'GreetingCardFees',
          customGiftCardFees: 'CustomGiftCardFees',
          seasonalGiftCardFees: 'SeasonalGiftCardFees',
          courierFees: 'Courier',
          administrationFees: 'AdministrationFees',
          busTicketsSum: 'BusTickets',
          carparksSum: 'Carparks',
          kiddyCruisersSum: 'KiddyCruisers',
          carValetSum: 'CarValet',
          parkingNMSum: 'ParkingNM',
          handsFreeSum: 'HandsFree',
          deliveryServiceSum: 'DeliveryService',
          miscellanousSum: 'Miscellanous',
          carWashSum: 'CarWash',
          productSum: 'Total',
          tenderCashSum: 'Cash',
          tenderEftposSum: 'Eftpos',
          tenderVisaSum: 'Visa',
          tenderMcardSum: 'MasterCard',
          tenderAmexSum: 'Amex',
          tenderDinersSum: 'Diners',
          tenderGiftCardSum: 'GiftCards',
          tenderDirectCreditSum: 'DirectCredit',
          tenderAllSum: 'TenderTotal',
          orderColumn: 'GroupID',
        }

        const currencyKeys = new Set([
          'giftCardSalesSum',
          'serviceFees',
          'greetingCardFees',
          'customGiftCardFees',
          'seasonalGiftCardFees',
          'courierFees',
          'administrationFees',
          'busTicketsSum',
          'carparksSum',
          'kiddyCruisersSum',
          'carValetSum',
          'parkingNMSum',
          'handsFreeSum',
          'deliveryServiceSum',
          'miscellanousSum',
          'carWashSum',
          'productSum',
          'tenderCashSum',
          'tenderEftposSum',
          'tenderVisaSum',
          'tenderMcardSum',
          'tenderAmexSum',
          'tenderDinersSum',
          'tenderGiftCardSum',
          'tenderDirectCreditSum',
          'tenderAllSum',
        ])

        const formatCurrency = (value: unknown): string => {
          if (value === null || value === undefined || value === '') return ''
          const num = Number(value)
          if (Number.isNaN(num)) return String(value)
          return `$${num.toFixed(2)}`
        }

        const csvData = report19Data.data.map((row: Report19CSVDataTypes) =>
          Object.fromEntries(
            csvKeys.map((key) => {
              const mappedKey = headerMapping[key] || key
              const value = row[key as keyof Report19CSVDataTypes]
              return [
                mappedKey,
                currencyKeys.has(key)
                  ? formatCurrency(value)
                  : value,
              ]
            })
          )
        )

        return (
          <div
            ref={resultRef}
            className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
          >
            <div>
              <p className="font-bold text-lg tracking-wide text-primary">
                Results:
              </p>
              <p className="tracking-wide text-primary font-light">
                The report is ready to download.
              </p>
            </div>

            <Button
              type="button"
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={() =>
                ExportToXLSX(csvData, {
                  filename: `Report19_${form.getValues('startDate')}_${form.getValues('endDate')}.xlsx`,
                })
              }
            >
              Download Report
            </Button>
          </div>
        )
      })() : null}
    </form>
  )
}
